"use client";

import { Brain, Database, Globe2, PanelsTopLeft } from "lucide-react";

import { PluginIcon, type PluginIconPlugin } from "@/components/content/plugin-icon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  sourceUsageDescription,
  sourceUsageMetric,
  type RunSourceUsage,
} from "@/lib/run-source-usage";
import { cn } from "@/lib/utils";
// Relative on purpose: this file crosses into the marketing build, whose
// narrow path maps do not cover @/components/app/*.
import { TwinMark } from "./twin-mark";

/**
 * Presentational half of RunSourcesUsed: the "Retrieved with" label and the
 * overlapping source avatars, rendered entirely from props. The container
 * (`run-sources-used.tsx`) computes the usage from run events and resolves the
 * runtime plugin catalogue; surfaces without live data — print, export, the
 * marketing product bridge — pass fixtures instead, and their module graph
 * never reaches the app's data clients.
 */
export function RunSourcesUsedView(props: {
  sources: RunSourceUsage[];
  plugins?: readonly PluginIconPlugin[];
  className?: string;
}) {
  if (props.sources.length === 0) return null;
  const pluginsBySlug = new Map(
    (props.plugins ?? []).map((plugin) => [plugin.slug, plugin]),
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn("flex items-center gap-1.5", props.className)}>
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Retrieved with
        </span>
        <div className="flex items-center -space-x-1.5">
          {props.sources.map((source) => {
            const plugin = source.pluginSlug
              ? pluginsBySlug.get(source.pluginSlug)
              : undefined;
            const label = plugin?.label ?? source.label;
            const metric = sourceUsageMetric(source);
            return (
              <Tooltip key={source.key}>
                <TooltipTrigger asChild>
                  <span
                    aria-label={`${label}, ${metric}`}
                    className="flex size-5 items-center justify-center rounded-full border border-border bg-background ring-1 ring-background"
                    tabIndex={0}
                  >
                    {plugin ? (
                      <PluginIcon plugin={plugin} className="size-3" />
                    ) : (
                      <RunSourceIcon source={source} />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{sourceUsageDescription(source)}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}

function RunSourceIcon(props: { source: RunSourceUsage }) {
  const className = "size-3 text-muted-foreground";
  switch (props.source.kind) {
    case "web":
      return <Globe2 className={className} />;
    case "warehouse":
    case "plugin":
      return <Database className={className} />;
    case "workspace":
      return <PanelsTopLeft className={className} />;
    case "memory":
      return <Brain className={className} />;
    case "twin":
      return <TwinMark className={className} />;
  }
}
